import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { backendUrl } from "../Router/constant";

const initialFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
};

const variantConfig = {
  contact: {
    formClassName: "contact-form",
    gridClassName: "contact-form__grid",
    fieldClassName: "contact-form__field",
    submitClassName: "contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100",
    phonePlaceholderKey: "common.forms.phonePlaceholder",
  },
  private: {
    formClassName: "private-sector-form",
    gridClassName: "private-sector-form__grid",
    fieldClassName: "private-sector-form__field",
    submitClassName:
      "contact-btn private-sector-form__submit d-flex align-items-center f-w-600 f-s-14 l-h-100",
    phonePlaceholderKey: "common.forms.intlPhonePlaceholder",
  },
};

export default function ContactForm({
  variant = "contact",
  website = "provestCapital",
  formClassName = "",
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const config = variantConfig[variant] || variantConfig.contact;

  const validate = () => {
    const newErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = t("common.forms.validation.firstNameRequired");
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = t("common.forms.validation.lastNameRequired");
    }

    if (!form.email.trim()) {
      newErrors.email = t("common.forms.validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t("common.forms.validation.invalidEmail");
    }

    if (!form.phone.trim()) {
      newErrors.phone = t("common.forms.validation.phoneRequired");
    } else if (!/^[0-9+\-\s]+$/.test(form.phone)) {
      newErrors.phone = t("common.forms.validation.invalidPhone");
    }

    if (!form.message.trim()) {
      newErrors.message = t("common.forms.validation.messageRequired");
    }

    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await fetch(`${backendUrl}auth/quim-contact-support`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phoneNumber: form.phone,
          message: form.message,
          website,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setForm(initialFormState);
        toast.success(data?.data?.message || t("common.feedback.messageSent"));
      } else {
        toast.error(data?.data?.message || t("common.feedback.somethingWrong"));
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(t("common.feedback.serverError"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const combinedFormClassName = [config.formClassName, formClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <form className={combinedFormClassName} onSubmit={handleSubmit} noValidate>
      <div className={config.gridClassName}>
        <label className={config.fieldClassName}>
          <span>{t("common.forms.firstName")}</span>
          <input
            type="text"
            name="firstName"
            className={errors.firstName ? "is-invalid" : ""}
            placeholder={t("common.forms.firstName")}
            value={form.firstName}
            onChange={handleChange}
          />
          {errors.firstName ? (
            <div className="invalid-feedback">{errors.firstName}</div>
          ) : null}
        </label>

        <label className={config.fieldClassName}>
          <span>{t("common.forms.lastName")}</span>
          <input
            type="text"
            name="lastName"
            className={errors.lastName ? "is-invalid" : ""}
            placeholder={t("common.forms.lastName")}
            value={form.lastName}
            onChange={handleChange}
          />
          {errors.lastName ? (
            <div className="invalid-feedback">{errors.lastName}</div>
          ) : null}
        </label>
      </div>

      <label className={config.fieldClassName}>
        <span>{t("common.forms.email")}</span>
        <input
          type="email"
          name="email"
          className={errors.email ? "is-invalid" : ""}
          placeholder={t("common.forms.emailPlaceholder")}
          value={form.email}
          onChange={handleChange}
        />
        {errors.email ? <div className="invalid-feedback">{errors.email}</div> : null}
      </label>

      <label className={config.fieldClassName}>
        <span>{t("common.forms.phoneNumber")}</span>
        <input
          type="tel"
          name="phone"
          className={errors.phone ? "is-invalid" : ""}
          placeholder={t(config.phonePlaceholderKey)}
          value={form.phone}
          onChange={handleChange}
        />
        {errors.phone ? <div className="invalid-feedback">{errors.phone}</div> : null}
      </label>

      <label className={config.fieldClassName}>
        <span>{t("common.forms.message")}</span>
        <textarea
          name="message"
          className={errors.message ? "is-invalid" : ""}
          placeholder={t("common.forms.messagePlaceholder")}
          value={form.message}
          onChange={handleChange}
        />
        {errors.message ? (
          <div className="invalid-feedback">{errors.message}</div>
        ) : null}
      </label>

      <button className={config.submitClassName} type="submit" disabled={loading}>
        <span className="btn-text contact-sub">
          {loading ? t("common.feedback.sending") : t("common.cta.submit")}
        </span>
        <span className="arrow-circle">
          <img src="/images/right-angle-arrow.png" alt="icon" />
        </span>
      </button>
    </form>
  );
}
